import React, { useState, useEffect } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, getDocs, addDoc, orderBy, arrayUnion } from './firestore-compat';
import RecusaConviteModal from './components/RecusaConviteModal';
import AddAuditoriaModal from './AddAuditoriaModal';
import Notificacoes from './Notificacoes';
import { getUserRoles, getUserRolesByEmail, getTargetAgenda, ROLES } from './utils/roleUtils';

function Convites() {
    const [convites, setConvites] = useState([]);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isRecusaModalOpen, setIsRecusaModalOpen] = useState(false);
    const [conviteParaRecusar, setConviteParaRecusar] = useState(null);
    const [activeTab, setActiveTab] = useState('convites'); // 'convites' ou 'notificacoes'
    const [unreadNotesCount, setUnreadNotesCount] = useState(0);
    const currentUser = auth.currentUser;

    useEffect(() => {
        if (!currentUser) return;
        const qNotif = query(
            collection(db, 'notificacoes'),
            where('destinatarioUid', '==', currentUser.uid),
            where('lida', '==', false)
        );
        const unsubNotif = onSnapshot(qNotif, (snapshot) => {
            setUnreadNotesCount(snapshot.docs.length);
        });
        return () => unsubNotif();
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) return;

        const fetchData = async () => {
            const qU = query(collection(db, getCollectionName('users')), where('email', '==', currentUser.email));
            const snapU = await getDocs(qU);
            if (snapU.empty) {
                setLoading(false);
                return;
            }
            const uData = snapU.docs[0].data();
            setUserData(uData);
            const myAgendaName = uData.agendaName || uData.name;

            // Normalizar roles: array ou string legada
            const userRoles = Array.isArray(uData.roles) ? uData.roles : (uData.role ? [uData.role] : []);
            // Detecção ampla de secretária: role, permissão, ou nome/email conhecidos
            const SECRETARY_ROLES = ['secretaria', 'secretaria_diretoria', 'admin', 'administrador', 'Secretária', 'Secretaria', 'secretária'];
            const SECRETARY_EMAILS = ['secretaria@prontobaby.com.br', 'nucleoadministrativo@prontobaby.com.br']; // Adicionar emails conhecidos
            const isSecretary = userRoles.some(r => SECRETARY_ROLES.includes(r)) ||
                uData.permissions?.agenda_direcao?.edit === true ||
                SECRETARY_EMAILS.includes(currentUser.email);
            const isAuditor = uData.permissions?.agenda_qualidade?.edit === true;

            const collectionsToWatch = [
                { name: getCollectionName('auditorias'), label: 'Qualidade', participantField: 'auditores', color: '#28a745' },
                { name: getCollectionName('agendaDirecao'), label: 'Direção', participantField: 'responsaveis', color: '#ffc107' },
                { name: getCollectionName('auditorias_homecare'), label: 'Homecare', participantField: 'auditores', color: '#6f42c1' },
                { name: getCollectionName('agenda_pessoal'), label: 'Pessoal', participantField: 'participantesUids', color: '#00897b' }
            ];

            const DIRECAO_NAMES = ['Ana Paula', 'Dr. Mário', 'Diretoria'];

            // Buscar UIDs dos diretores para cruzar com participantesUids
            const DIRECAO_EMAILS = ['apaulacf2020@gmail.com', 'mario@prontobaby.com.br'];
            const direcaoUids = [];
            for (const email of DIRECAO_EMAILS) {
                const snapDir = await getDocs(query(collection(db, getCollectionName('users')), where('email', '==', email)));
                snapDir.forEach(d => direcaoUids.push(d.id));
            }
            // Também por nome
            for (const dirName of DIRECAO_NAMES) {
                const snapDir = await getDocs(query(collection(db, getCollectionName('users')), where('name', '==', dirName)));
                snapDir.forEach(d => { if (!direcaoUids.includes(d.id)) direcaoUids.push(d.id); });
            }
            console.log('[CONVITES DEBUG] user:', currentUser.email, 'roles:', userRoles, 'permissions:', JSON.stringify(uData.permissions), 'isSecretary:', isSecretary, 'direcaoUids:', direcaoUids);

            const unsubscribes = collectionsToWatch.map(coll => {
                // Secretárias veem os PENDENTES da direção para aprovação
                const statusToWatch = (coll.name === getCollectionName('agendaDirecao') && isSecretary) ? 'Pendente' : 'Agendado';

                const qArgs = [
                    collection(db, coll.name),
                    where('conviteSistema', '==', true)
                ];

                // NÃO filtrar status na query para AgendaDirecao, pois pode haver mix de Pendente/Agendado
                // Para outras agendas, mantemos o padrão 'Agendado' para otimizar, a menos que seja auditoria
                if (coll.name !== getCollectionName('agendaDirecao')) {
                    qArgs.push(where('status', '==', 'Agendado'));
                }

                const q = query(...qArgs);

                return onSnapshot(q, (snapshot) => {
                    console.log(`[DEBUG] Convites snapshot from ${coll.name}: ${snapshot.size} docs`);
                    const docs = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                        sourceColl: coll.name,
                        sourceLabel: coll.label,
                        sourceColor: coll.color,
                        participantField: coll.participantField
                    }));

                    setConvites(prev => {
                        const otherColls = prev.filter(item => item.sourceColl !== coll.name);

                        // Ler lista de ignorados/recusados forçadamente
                        const ignoredInvites = JSON.parse(localStorage.getItem('ignored_invites') || '{}');

                        const filteredNew = docs.filter(item => {
                            // Se estiver na lista negra local, ignora
                            if (ignoredInvites[item.id]) {
                                return false;
                            }

                            const participants = item[coll.participantField] || [];
                            const internalParticipants = item.participantesUids || [];
                            const myUid = currentUser.uid;

                            // REGRA ANTI AUTO-CONVITE: Se eu criei o evento, não preciso de convite
                            const isMyCreation = item.criadoPorUid === myUid || item.createdBy === myUid || item.ownerUid === myUid;
                            if (isMyCreation) return false;

                            const myAgendaName = uData.agendaName || uData.name;
                            const isDirector = direcaoUids.includes(myUid) ||
                                DIRECAO_NAMES.some(d => d.includes(myAgendaName) || myAgendaName.includes(d));

                            // REGRA: Apenas diretores não vêem convites originários da agendaDirecao (vêem direto na agenda).
                            // Participantes comuns (Auditores, etc) devem ver o convite.
                            if (coll.name === getCollectionName('agendaDirecao') && isDirector) return false;

                            // Lógica de Visibilidade Refinada:
                            const alreadyAcceptedByUid = item.aceitePorUids?.includes(myUid);
                            const alreadyAcceptedByName = item.aceitePor?.includes(myAgendaName);

                            // Verificar se já recusou este convite (por UID, Nome ou Email)
                            const myEmail = currentUser.email;
                            const alreadyRefused = item.recusas?.some(r =>
                                r.uid === myUid ||
                                r.nome === myAgendaName ||
                                (r.email && r.email === myEmail)
                            );

                            // 1. É PARTICIPANTE DIRETO?
                            let isInvitedByName = false;

                            // Juntar todos os nomes possíveis que representam os convidados deste cartão
                            const checkNameStrs = [];
                            if (Array.isArray(participants)) {
                                checkNameStrs.push(...participants);
                            } else if (typeof participants === 'string') {
                                checkNameStrs.push(participants);
                            }
                            // Adicionar os nomes internos (que vieram da seleção no modal e que nem sempre aparecem com destaque)
                            if (Array.isArray(item.participantesNomes)) {
                                checkNameStrs.push(...item.participantesNomes);
                            }

                            if (myAgendaName && myAgendaName.trim() !== '') {
                                isInvitedByName = checkNameStrs.some(n => typeof n === 'string' && n.includes(myAgendaName));
                            }

                            const isSuperAdmin = currentUser.email === 'alexandrebareaoliveira@gmail.com' || userRoles.includes('admin') || userRoles.includes('administrador');

                            // Para agenda_pessoal, UID é o principal. Para as outras, exigimos nome visível ou que o UID esteja explicitamente lá
                            let isInvited = false;

                            if (coll.name === getCollectionName('agenda_pessoal')) {
                                isInvited = internalParticipants.includes(myUid) || isInvitedByName;
                            } else {
                                // Se for um admin supremo, ele SÓ quer ver eventos onde o NOME dele aparece de forma audível.
                                // Não queremos que ele receba lixo só porque o UID dele escorregou para dentro de `participantesUids` ao criar o evento
                                if (isSuperAdmin) {
                                    isInvited = isInvitedByName;
                                } else {
                                    isInvited = internalParticipants.includes(myUid) || isInvitedByName;
                                }
                            }

                            if (isInvited) {
                                // Participante direto só vê se ainda não aceitou nem recusou
                                return !alreadyAcceptedByUid && !alreadyAcceptedByName && !alreadyRefused;
                            }

                            // ===== 2. REGRA SECRETÁRIA (para eventos de terceiros) =====
                            // Se não for participante direto, só vê se for Secretária analisando um evento PENDENTE da Diretoria.
                            if (isSecretary && !isDirector) {
                                // Para as secretárias de verdade, mostramos os PENDENTES da diretoria:
                                // Para super admins, NÃO MAIS mostramos, a menos que sejam convidados diretamente (regra 1).
                                if (isSuperAdmin && !SECRETARY_EMAILS.includes(currentUser.email)) {
                                    return false; // Quebra a regra da secretaria para quem é apenas TI/Admin Sistema
                                }

                                const itemPartNomes = item.participantesNomes || [];
                                const itemPartUids = item.participantesUids || [];
                                const itemAuditores = item.auditores || [];
                                const itemResponsaveis = item.responsaveis || [];

                                let hasDirecaoByName = false;
                                const checkNames = [...itemPartNomes, ...itemAuditores, ...itemResponsaveis];
                                if (Array.isArray(participants)) {
                                    checkNames.push(...participants);
                                } else if (typeof participants === 'string') {
                                    checkNames.push(participants);
                                }

                                hasDirecaoByName = checkNames.some(p => typeof p === 'string' && DIRECAO_NAMES.some(d => p.includes(d) || d.includes(p)));
                                const hasDirecaoByUid = itemPartUids.some(uid => direcaoUids.includes(uid));

                                if (hasDirecaoByName || hasDirecaoByUid) {
                                    // SÓ exibe se estiver Pendente (aguardando aprovação da secretaria).
                                    // Se já for 'Agendado' e a secretaria não é participante, não tem porque ver na Caixa de Entrada.
                                    if (item.status === 'Pendente') {
                                        return true;
                                    }
                                }
                            }

                            return false; // Se chegou aqui, não tem que ver o convite
                        });
                        return [...otherColls, ...filteredNew].sort((a, b) => (a.data || '').localeCompare(b.data || ''));
                    });
                    setLoading(false);
                }, (error) => {
                    console.error(`Erro ao buscar convites de ${coll.name}:`, error);
                    setLoading(false);
                });
            });

            return () => unsubscribes.forEach(unsub => unsub());
        };

        fetchData();
    }, [currentUser]);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [draftEvent, setDraftEvent] = useState(null);
    const [draftOriginalRef, setDraftOriginalRef] = useState(null);
    const [draftOriginalId, setDraftOriginalId] = useState(null);
    const [draftOriginalColl, setDraftOriginalColl] = useState(null);
    const [allUsersConfig, setAllUsersConfig] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            const qAllUsers = query(collection(db, getCollectionName('users')), orderBy('name'));
            const snapAllUsers = await getDocs(qAllUsers);
            const allUsersList = snapAllUsers.docs.map(d => ({
                id: d.id,
                name: d.data().name || d.data().email,
                color: d.data().color || '#00897b' // Fallback color
            }));
            setAllUsersConfig(allUsersList);
        };
        fetchUsers();
    }, []);

    const handleAceitarClick = async (convite) => {
        // Preparar dados para o modal
        const initialData = {
            descricao: convite.descricao || convite.auditoria,
            data: convite.data,
            horario: convite.horario,
            categoria: convite.categoria || 'Agendamento',
            unidade: convite.unidade || convite.setor,
            setor: convite.setor,
            participantes: convite.participantes,
            participantesUids: convite.participantesUids, // Passa mas o modal vai desativar notificação
            participantesNomes: convite.participantesNomes || [], // Passar nomes para a cópia na agendaDirecao
            auditores: convite.auditores || (convite.auditor ? [convite.auditor] : [])
        };

        setDraftEvent(initialData);
        setDraftOriginalId(convite.id);
        setDraftOriginalColl(convite.sourceColl);
        // Referência guardada apenas para logs ou uso futuro se precisar

        setIsAddModalOpen(true);
    };

    const handleConfirmarAceite = async (novoEventoData) => {
        try {
            console.log("Processando aceite de convite (Unified Agenda):", novoEventoData);

            // 1. Dados do usuário atual
            const myUid = currentUser.uid;
            const myUserName = userData.name || userData.email;

            // --- NOVO: Verificação de conflito de agenda ---
            const { checkScheduleConflict } = await import('./utils/conflictUtils');
            const conflictResult = await checkScheduleConflict(
                myUid,
                myUserName,
                novoEventoData.data,
                novoEventoData.horario,
                draftOriginalId
            );

            if (conflictResult.hasConflict) {
                const event = conflictResult.conflictEvent;
                const confirmRes = window.confirm(
                    `⚠️ CONFLITO DE AGENDA DETECTADO!\n\n` +
                    `Você já possui um compromisso agendado para este horário:\n` +
                    `• Evento: ${event.descricao || event.auditoria}\n` +
                    `• Horário: ${event.horario}\n` +
                    `• Local/Setor: ${event.setor || event.unidade || 'Não informado'}\n\n` +
                    `Deseja aceitar mesmo assim (gerando duplicidade) ou deseja cancelar?`
                );

                if (!confirmRes) {
                    console.log("[ACEITE] Cancelado pelo usuário devido a conflito.");
                    return;
                }
            }
            // ----------------------------------------------

            // 2. AÇÃO ÚNICA: Atualizar o documento ORIGINAL
            // Em vez de criar cópias, nós apenas "carimbamos" o aceite no documento original.
            // A 'MinhaAgenda.js' agora é capaz de ler este documento original remotamente.

            const conviteRef = doc(db, draftOriginalColl, draftOriginalId);

            const updates = {
                aceitePor: arrayUnion(myUserName),
                aceitePorUids: arrayUnion(myUid),
                // Se for 'convites' (legado ou sistema antigo), manter statusConvite
                statusConvite: 'Aceito'
            };

            // Se for Agenda Direção e estiver Pendente, o aceite da Secretária/Diretor oficializa para Agendado
            if (draftOriginalColl === getCollectionName('agendaDirecao')) {
                // Regra: Se quem aceita é secretária ou diretor, e status é Pendente -> vira Agendado
                // Mas user pediu revisão de dados. O novoEventoData tem updates?
                // Sim, o modal permite editar.

                // Merge dos dados editados (se permitido pela regra de segurança)
                Object.assign(updates, {
                    descricao: novoEventoData.descricao,
                    horario: novoEventoData.horario,
                    data: novoEventoData.data, // Caso mude a data
                    status: 'Agendado' // Confirma o evento
                });
            } else {
                // Para outras agendas, se houver edição de campos permitidos (descrição, etc), aplicamos
                // Isso depende se o usuário mudou algo no modal.
                // Assumindo que sim:
                if (novoEventoData.descricao) updates.descricao = novoEventoData.descricao;
                if (novoEventoData.horario) updates.horario = novoEventoData.horario;
            }

            console.log('[ACEITE] Atualizando doc:', draftOriginalColl, draftOriginalId, 'updates:', JSON.stringify(updates));
            await updateDoc(conviteRef, updates);
            console.log('[ACEITE] Doc atualizado com sucesso');

            // ===== COPIAR PARA agendaDirecao SE DIRETOR É PARTICIPANTE =====
            // Quando o evento original é de outra coleção (ex: auditorias) e tem diretores,
            // criamos uma cópia na agendaDirecao para que apareça na grade da Direção.
            if (draftOriginalColl !== getCollectionName('agendaDirecao')) {
                try {
                    const DIRECAO_NAMES_LOCAL = ['Ana Paula', 'Dr. Mário', 'Diretoria'];
                    const DIRECAO_EMAILS_LOCAL = ['apaulacf2020@gmail.com', 'mario@prontobaby.com.br'];

                    // Buscar UIDs dos diretores
                    const direcaoUidsLocal = [];
                    for (const email of DIRECAO_EMAILS_LOCAL) {
                        const snapD = await getDocs(query(collection(db, getCollectionName('users')), where('email', '==', email)));
                        snapD.forEach(d => direcaoUidsLocal.push(d.id));
                    }

                    const partNomes = novoEventoData.participantesNomes || [];
                    const partUids = novoEventoData.participantesUids || [];
                    const auditoresEvt = novoEventoData.auditores || [];

                    const hasDirByName = [...partNomes, ...auditoresEvt]
                        .some(p => typeof p === 'string' && DIRECAO_NAMES_LOCAL.some(d => p.includes(d) || d.includes(p)));
                    const hasDirByUid = partUids.some(uid => direcaoUidsLocal.includes(uid));

                    if (hasDirByName || hasDirByUid) {
                        // Simplificação: usar os nomes que batem
                        const responsaveisDirecao = [...partNomes, ...auditoresEvt]
                            .filter(p => typeof p === 'string' && DIRECAO_NAMES_LOCAL.some(d => p.includes(d) || d.includes(p)));
                        // Se não achou por nome, tentar por UID (workaround: colocar genérico)
                        const finalResponsaveis = responsaveisDirecao.length > 0 ? responsaveisDirecao : ['Diretoria'];

                        await addDoc(collection(db, 'agendaDirecao'), {
                            descricao: novoEventoData.descricao || novoEventoData.auditoria || 'Compromisso Externo',
                            data: novoEventoData.data,
                            horario: novoEventoData.horario || '08:00',
                            responsaveis: finalResponsaveis,
                            status: 'Agendado',
                            participantesUids: partUids,
                            participantesNomes: partNomes,
                            sourceRef: draftOriginalId,
                            sourceCollection: draftOriginalColl,
                            isExternalCopy: true, // Flag para saber que é cópia
                            criadoPorUid: myUid,
                            criadoPorNome: myUserName
                        });
                        console.log('[CONVITES] Evento copiado para agendaDirecao com sucesso!');
                    }
                } catch (copyErr) {
                    console.error('[CONVITES] Erro ao copiar para agendaDirecao:', copyErr);
                    // Não bloquear o aceite por falha na cópia
                }
            }

            // ===== COPIAR PARA auditorias SE A ACEITANTE É AUDITORA =====
            // Auditoras usam AgendaQualidade como "Minha Agenda" (que lê de `auditorias`).
            // Sem esta cópia, o evento aceito NÃO aparece na agenda delas.
            const isAuditor = userData?.permissions?.agenda_qualidade?.edit === true;
            if (isAuditor && draftOriginalColl !== getCollectionName('auditorias')) {
                try {
                    // Verificar se já existe uma cópia (evitar duplicatas em múltiplos aceites)
                    const qExist = query(
                        collection(db, getCollectionName('auditorias')),
                        where('sourceRef', '==', draftOriginalId),
                        where('auditor', '==', myUserName)
                    );
                    const snapExist = await getDocs(qExist);

                    if (snapExist.empty) {
                        await addDoc(collection(db, getCollectionName('auditorias')), {
                            descricao: novoEventoData.descricao || 'Compromisso',
                            auditoria: novoEventoData.descricao || 'Compromisso',
                            auditor: myUserName,
                            auditores: [myUserName],
                            data: novoEventoData.data,
                            horario: novoEventoData.horario || '09:00',
                            setor: novoEventoData.setor || '',
                            unidade: novoEventoData.unidade || '',
                            categoria: novoEventoData.categoria || 'Agendamento',
                            status: 'Agendado',
                            conviteSistema: false,  // Já é a cópia definitiva, sem reenviar convites
                            participantesUids: novoEventoData.participantesUids || [],
                            participantesNomes: novoEventoData.participantesNomes || [],
                            sourceRef: draftOriginalId,              // Referência para evitar duplicatas
                            sourceCollection: draftOriginalColl,
                            isAcceptedCopy: true,                    // Flag: veio de aceite de convite
                            criadoPorUid: myUid,
                            criadoPorNome: myUserName
                        });
                        console.log('[CONVITES] Evento copiado para auditorias (auditora aceitou convite pessoal)!');
                    } else {
                        console.log('[CONVITES] Cópia em auditorias já existe, pulando duplicata.');
                    }
                } catch (auditorCopyErr) {
                    console.error('[CONVITES] Erro ao copiar para auditorias:', auditorCopyErr);
                    // Não bloquear o aceite por falha na cópia
                }
            }

            // ===== COPIAR PARA agenda_pessoal SE É USUÁRIO COMUM (não auditor) =====
            // Usuários comuns (ex: Brunna) usam MinhaAgenda que lê agenda_pessoal com filtro createdBy == uid.
            // Sem esta cópia, o evento aceito de terceiros NÃO aparece na agenda deles.
            // Cobre TODOS os casos: convites de agendaDirecao, auditorias E convites de agenda_pessoal de terceiros.
            const isConviteDeOutraPessoa = draftOriginalColl === getCollectionName('agenda_pessoal');
            // Se o convite é da própria agenda_pessoal, precisamos criar uma cópia APENAS se o criador é outra pessoa
            // (situação: Fulano criou um evento pessoal e convidou a Brunna)
            const precisaCopiaAgendaPessoal = !isAuditor && (
                draftOriginalColl !== getCollectionName('agenda_pessoal') ||
                isConviteDeOutraPessoa  // sempre clona para garantir que aparece no filtro createdBy
            );

            if (precisaCopiaAgendaPessoal) {
                try {
                    // Verificar se já existe cópia para evitar duplicatas
                    const qExistPessoal = query(
                        collection(db, getCollectionName('agenda_pessoal')),
                        where('sourceRef', '==', draftOriginalId),
                        where('createdBy', '==', myUid)
                    );
                    const snapExistPessoal = await getDocs(qExistPessoal);

                    if (snapExistPessoal.empty) {
                        await addDoc(collection(db, getCollectionName('agenda_pessoal')), {
                            descricao: novoEventoData.descricao || 'Compromisso',
                            data: novoEventoData.data,
                            horario: novoEventoData.horario || '09:00',
                            setor: novoEventoData.setor || '',
                            unidade: novoEventoData.unidade || '',
                            categoria: novoEventoData.categoria || 'Agendamento',
                            status: 'Agendado',
                            conviteSistema: false,       // Cópia definitiva, não gera novos convites
                            participantesUids: novoEventoData.participantesUids || [],
                            participantesNomes: novoEventoData.participantesNomes || [],
                            sourceRef: draftOriginalId,  // Referência para evitar duplicatas
                            sourceCollection: draftOriginalColl,
                            isAcceptedCopy: true,        // Flag: veio de aceite de convite
                            createdBy: myUid,            // ESSENCIAL: campo exigido pela query de MinhaAgenda
                            criadoPorUid: myUid,
                            criadoPorNome: myUserName,
                            uid: myUid
                        });
                        console.log('[CONVITES] Evento copiado para agenda_pessoal (usuário comum aceitou convite)!');
                    } else {
                        console.log('[CONVITES] Cópia em agenda_pessoal já existe, pulando duplicata.');
                    }
                } catch (pessoalCopyErr) {
                    console.error('[CONVITES] Erro ao copiar para agenda_pessoal:', pessoalCopyErr);
                    // Não bloquear o aceite por falha na cópia
                }
            }

            alert('Convite aceito com sucesso! O evento foi adicionado à sua agenda.');
            setIsAddModalOpen(false);
            setDraftEvent(null);

        } catch (error) {
            console.error("Erro ao confirmar aceite:", error);
            alert("Erro ao processar aceite: " + error.message);
        }
    };

    // Mantendo handleAceitar original apenas como referência ou renomeando para não quebrar 
    // imports se houver, mas o botão vai chamar handleAceitarClick


    const handleRecusar = async (justificativa) => {
        if (!conviteParaRecusar) return;

        try {
            const myAgendaName = userData.agendaName || userData.name;
            const myUid = currentUser.uid;
            const convite = conviteParaRecusar;

            // Verificar se o evento tem criador definido
            const criadorUid = convite.criadoPorUid || convite.ownerUid || convite.createdBy;

            if (!criadorUid) {
                console.warn('Evento sem criador definido:', convite);
                alert('Não foi possível identificar o criador deste evento. A recusa será registrada mas sem notificação.');
            }

            // 1. Criar notificação para o criador do evento (se existir)
            if (criadorUid) {
                await addDoc(collection(db, 'notificacoes'), {
                    tipo: 'recusa_convite',
                    destinatarioUid: criadorUid,
                    lida: false,
                    dataNotificacao: new Date().toISOString(),

                    // Dados do evento
                    eventoId: convite.id,
                    eventoDescricao: convite.descricao || convite.auditoria || 'Evento',
                    eventoData: convite.data,
                    eventoHorario: convite.horario,
                    eventoCollection: convite.sourceColl,

                    // Dados da recusa
                    recusadoPorUid: myUid,
                    recusadoPorNome: myAgendaName,
                    justificativa: justificativa
                });
            }

            // 2. Atualizar o evento original com a recusa
            const conviteRef = doc(db, convite.sourceColl, convite.id);
            const recusasAtuais = convite.recusas || [];

            const recusaObj = {
                uid: myUid,
                nome: myAgendaName,
                email: currentUser.email,
                justificativa: justificativa,
                dataRecusa: new Date().toISOString(),
                notificacaoLida: false
            };

            console.log(`[RECUSA] Convite ${convite.id} recusado com sucesso`);

            try {
                await updateDoc(conviteRef, {
                    recusas: [...recusasAtuais, recusaObj]
                });
            } catch (err) {
                console.warn("[RECUSA] Erro ao atualizar doc original (pode ter sido excluído ou sem permissão). Forçando remoção local.", err);
                // Se der erro (ex: permissão ou doc não existe), salvamos no localStorage para não mostrar mais
                const ignoredInfo = JSON.parse(localStorage.getItem('ignored_invites') || '{}');
                ignoredInfo[convite.id] = { reason: 'update_failed_on_refuse', date: new Date().toISOString() };
                localStorage.setItem('ignored_invites', JSON.stringify(ignoredInfo));
            }

            // CRÍTICO: Remover o convite IMEDIATAMENTE do estado local
            console.log(`[RECUSA] Removendo convite ${convite.id} do estado local`);
            setConvites(prev => {
                const filtered = prev.filter(c => c.id !== convite.id);
                return filtered;
            });

            alert(`✅ Convite removido da sua lista com sucesso!`);

            setIsRecusaModalOpen(false);
            setConviteParaRecusar(null);
        } catch (error) {
            console.error("Erro CRÍTICO ao recusar convite:", error);
            // Mesmo com erro crítico, tenta ocultar
            const ignoredInfo = JSON.parse(localStorage.getItem('ignored_invites') || '{}');
            ignoredInfo[conviteParaRecusar.id] = { reason: 'critical_error_refuse', date: new Date().toISOString() };
            localStorage.setItem('ignored_invites', JSON.stringify(ignoredInfo));

            setConvites(prev => prev.filter(c => c.id !== conviteParaRecusar.id));

            alert('Houve um erro de comunicação com o servidor, mas o convite foi removido da sua visualização local.');
            setIsRecusaModalOpen(false);
        }
    };

    const handleOpenRecusaModal = (convite) => {
        setConviteParaRecusar(convite);
        setIsRecusaModalOpen(true);
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
            </div>
        </div>
    );

    return (
        <div className="convites-container p-4" style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
            <div className="page-header mb-4 text-center">
                <h2 style={{ fontWeight: '700', color: '#2d3436', fontSize: '2.2rem', marginBottom: '10px' }}>
                    Convites de Agenda
                </h2>
                <p className="text-muted" style={{ fontSize: '1.1rem' }}>Convites para compromissos (aceitar ou recusar) e avisos do sistema.</p>
            </div>

            {/* Custom Tabs Navigation */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                marginBottom: '30px',
                borderBottom: '2px solid #e1e9e8'
            }}>
                <button
                    onClick={() => setActiveTab('convites')}
                    style={{
                        background: 'none',
                        border: 'none',
                        borderBottom: activeTab === 'convites' ? '3px solid #0984e3' : '3px solid transparent',
                        padding: '10px 20px',
                        fontSize: '1.1rem',
                        fontWeight: activeTab === 'convites' ? '700' : '500',
                        color: activeTab === 'convites' ? '#0984e3' : '#636e72',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    ✉️ Convites Pendentes
                    {convites.length > 0 && (
                        <span style={{
                            backgroundColor: '#ff4757',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '2px 8px',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                        }}>
                            {convites.length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('notificacoes')}
                    style={{
                        background: 'none',
                        border: 'none',
                        borderBottom: activeTab === 'notificacoes' ? '3px solid #0984e3' : '3px solid transparent',
                        padding: '10px 20px',
                        fontSize: '1.1rem',
                        fontWeight: activeTab === 'notificacoes' ? '700' : '500',
                        color: activeTab === 'notificacoes' ? '#0984e3' : '#636e72',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    🔔 Avisos
                    {unreadNotesCount > 0 && (
                        <span style={{
                            backgroundColor: '#ff4757',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '2px 8px',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                        }}>
                            {unreadNotesCount}
                        </span>
                    )}
                </button>
            </div>

            {/* Tab Content: Convites */}
            {activeTab === 'convites' && (
                <div className="convites-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: '25px',
                    padding: '10px'
                }}>
                    {convites.length === 0 ? (
                        <div className="text-center p-5 bg-white rounded-4 shadow-sm" style={{ gridColumn: '1 / -1', border: '2px dashed #ddd' }}>
                            <div style={{ fontSize: '4rem', color: '#ddd', marginBottom: '15px' }}>📅</div>
                            <h4 style={{ color: '#636e72' }}>Tudo pronto!</h4>
                            <p className="text-muted mb-0">Você não possui convites pendentes no momento.</p>
                        </div>
                    ) : (
                        convites.map(convite => (
                            <div key={convite.id} className="convite-card-modern" style={{
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                overflow: 'hidden',
                                transition: 'all 0.3s ease',
                                border: '1px solid #e8e8e8',
                                cursor: 'pointer'
                            }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                }}
                            >
                                {/* Color bar */}
                                <div style={{
                                    height: '4px',
                                    background: convite.sourceColor
                                }}></div>

                                <div className="p-4">
                                    {/* Header with badge */}
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <span className="badge" style={{
                                            backgroundColor: convite.sourceColor,
                                            color: '#fff',
                                            padding: '6px 14px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            borderRadius: '20px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {convite.sourceLabel}
                                        </span>
                                    </div>

                                    {/* Title */}
                                    <h4 style={{
                                        color: '#2d3436',
                                        fontWeight: '700',
                                        marginBottom: '16px',
                                        fontSize: '18px',
                                        lineHeight: '1.4'
                                    }}>
                                        {convite.descricao || convite.auditoria || 'Sem descrição'}
                                    </h4>

                                    {/* Details */}
                                    <div className="convite-details" style={{
                                        fontSize: '14px',
                                        color: '#636e72',
                                        marginBottom: '16px'
                                    }}>
                                        <div className="d-flex align-items-center mb-2">
                                            <span style={{ width: '24px' }}>📅</span>
                                            <span className="fw-bold me-2">Data:</span>
                                            <span>{new Date(convite.data + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
                                        </div>
                                        <div className="d-flex align-items-center mb-2">
                                            <span style={{ width: '24px' }}>⏰</span>
                                            <span className="fw-bold me-2">Horário:</span>
                                            <span>{convite.horario}</span>
                                        </div>
                                        {convite.setor && (
                                            <div className="d-flex align-items-center mb-2">
                                                <span style={{ width: '24px' }}>📍</span>
                                                <span className="fw-bold me-2">Setor:</span>
                                                <span>{convite.setor}</span>
                                            </div>
                                        )}

                                        {/* Criado por */}
                                        {convite.criadoPorNome && (
                                            <div className="d-flex align-items-center mb-2">
                                                <span style={{ width: '24px' }}>👤</span>
                                                <span className="fw-bold me-2">Criado por:</span>
                                                <span>{convite.criadoPorNome}</span>
                                            </div>
                                        )}

                                        {/* Participantes - Melhorado para mostrar nomes */}
                                        <div className="mt-3 p-3 rounded" style={{
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '13px',
                                            borderLeft: `3px solid ${convite.sourceColor}`
                                        }}>
                                            <strong>Participantes:</strong> {(() => {
                                                // Tentar pegar nomes dos participantes
                                                const participantNames = convite.participantesNomes ||
                                                    convite[convite.participantField] ||
                                                    [];

                                                if (participantNames.length > 0) {
                                                    return participantNames.join(', ');
                                                }

                                                // Fallback: se só tiver UIDs
                                                if (convite.participantesUids?.length > 0) {
                                                    return `${convite.participantesUids.length} participante(s)`;
                                                }

                                                return 'Não especificado';
                                            })()}
                                        </div>
                                    </div>

                                    {/* Action buttons */}
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            className="btn w-100 py-3 rounded-3"
                                            style={{
                                                backgroundColor: convite.sourceColor,
                                                color: '#fff',
                                                fontWeight: '600',
                                                fontSize: '15px',
                                                border: 'none',
                                                transition: 'all 0.2s ease',
                                                flex: 1
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.02)';
                                                e.currentTarget.style.boxShadow = `0 4px 12px ${convite.sourceColor}40`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'scale(1)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                            onClick={() => handleAceitarClick(convite)}
                                        >
                                            ✓ Aceitar
                                        </button>

                                        <button
                                            className="btn w-100 py-3 rounded-3"
                                            style={{
                                                backgroundColor: '#fff',
                                                color: '#dc3545',
                                                fontWeight: '600',
                                                fontSize: '15px',
                                                border: '2px solid #dc3545',
                                                transition: 'all 0.2s ease',
                                                flex: 1
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#dc3545';
                                                e.currentTarget.style.color = '#fff';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#fff';
                                                e.currentTarget.style.color = '#dc3545';
                                            }}
                                            onClick={() => handleOpenRecusaModal(convite)}
                                        >
                                            ✗ Recusar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Tab Content: Notificacoes */}
            {activeTab === 'notificacoes' && (
                <div style={{ animation: 'fadeIn 0.3s ease' }}>
                    <Notificacoes isEmbedded={true} />
                </div>
            )}

            <RecusaConviteModal
                isOpen={isRecusaModalOpen}
                onClose={() => {
                    setIsRecusaModalOpen(false);
                    setConviteParaRecusar(null);
                }}
                onRecusar={handleRecusar}
                convite={conviteParaRecusar}
            />

            <AddAuditoriaModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onAddAuditoria={handleConfirmarAceite}
                initialValues={draftEvent}
                type="pessoal"
                disableSystemInvite={true}
                auditoresConfig={allUsersConfig}
                submitLabel="Confirmar Presença"
            />
        </div>
    );
}

export default Convites;
